import { uiPaper } from "shared/ui/uiSurface"
import {
  Chat,
  Close,
  Email,
  HelpOutline,
  LocalShipping,
  Phone,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { selectProfile } from "src/features/profile/profileSlice"
import { useGetProviderDetailQuery } from "../apis/appApi"
import { useAppSelector } from "../hooks/hooks"

type InfoDialogProps = {
  open: boolean
  onClose: () => void
}

const ContactRow: FC<{
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}> = ({ icon, title, children }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
    <Box sx={{ color: "primary.main", mt: 0.25 }}>{icon}</Box>
    <Box sx={{ minWidth: 100 }}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Stack>
)

export const InfoDialog: FC<InfoDialogProps> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()
  const profile = useAppSelector(selectProfile)
  const { data: provider, isLoading } = useGetProviderDetailQuery(undefined, {
    skip: !open,
  })

  const providerCode = profile?.provider?.code ?? "\u2014"
  const providerName = profile?.provider?.name ?? "\u2014"
  const address = provider?.address
  const lang = i18n.language === "fr" ? "fr" : "en"
  const termsUrl = `https://conditions.mobilus.com/${lang}/terms.html`

  return (
    <Dialog
      data-testid="info-dialog"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          ...uiPaper("UI-DLV2-010"),
          sx: { borderRadius: 3, overflow: "hidden" },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutline fontSize="small" />
          <Typography variant="h6" fontWeight={600}>
            {t("info.title")}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip
            label={`${t("info.providerCode")}: ${providerCode}`}
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
            }}
          />
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#fff" }}
            data-testid="info-dialog-close-btn"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Contact section */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            letterSpacing={1}
          >
            {t("info.contactUs")}
          </Typography>
        </Box>

        <Stack
          divider={
            <Box sx={{ borderBottom: 1, borderColor: "divider", mx: 3 }} />
          }
          sx={{ px: 3 }}
        >
          {/* Online Help */}
          <ContactRow
            icon={<HelpOutline fontSize="small" />}
            title={t("info.onlineHelp")}
          >
            <Link
              href="https://help.mobilus.com"
              target="_blank"
              rel="noopener"
              variant="body2"
              underline="hover"
              data-testid="info-dialog-help-link"
            >
              {t("info.onlineHelpLink")}
            </Link>
          </ContactRow>

          {/* Chat */}
          <ContactRow icon={<Chat fontSize="small" />} title={t("info.byChat")}>
            <Link
              href="#"
              variant="body2"
              underline="hover"
              data-testid="info-dialog-chat-link"
            >
              {t("info.startSession")}
            </Link>
          </ContactRow>

          {/* Phone */}
          <ContactRow
            icon={<Phone fontSize="small" />}
            title={t("info.byPhone")}
          >
            <Stack spacing={0.25}>
              <Link
                href="tel:+18888077903"
                variant="body2"
                underline="hover"
                fontWeight={600}
                data-testid="info-dialog-phone-link"
              >
                +1 (888) 807-7903
              </Link>
              <Typography variant="caption" color="text.secondary">
                {t("info.phoneHours")}
              </Typography>
            </Stack>
          </ContactRow>

          {/* Email */}
          <ContactRow
            icon={<Email fontSize="small" />}
            title={t("info.byEmail")}
          >
            <Link
              href="mailto:support@mobilus.com"
              variant="body2"
              underline="hover"
              data-testid="info-dialog-email-link"
            >
              support@mobilus.com
            </Link>
          </ContactRow>
        </Stack>

        {/* Provider section */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            letterSpacing={1}
          >
            {t("info.providerSection")}
          </Typography>
        </Box>

        <Box sx={{ mx: 3, mb: 2.5, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <LocalShipping
                fontSize="small"
                sx={{ color: "primary.main", mt: 0.25 }}
              />
              <Stack spacing={0.25}>
                <Typography variant="body2" fontWeight={600}>
                  {providerName}
                </Typography>
                {address && (
                  <Typography variant="caption" color="text.secondary">
                    {[
                      [address.streetNumber, address.streetName]
                        .filter(Boolean)
                        .join(" "),
                      address.city,
                      address.state,
                      address.postcode,
                      address.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Terms footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            bgcolor: "grey.50",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t("info.terms")}{" "}
            <Link
              href={termsUrl}
              target="_blank"
              rel="noopener"
              variant="caption"
              underline="hover"
              data-testid="info-dialog-terms-link"
            >
              {termsUrl}
            </Link>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
